import { useCallback, useEffect, useMemo, useState } from "react";
import { getCurrentUser } from "aws-amplify/auth";
import { client } from "../../amplify-client";
import {
  listHiddenConversationEntriesForUser,
  unhideConversationForUser,
} from "../../api/communication/conversation-visibility-service";
import { publishCommunicationEvent } from "../../api/communication/communication-events-service";
import { getProfileSummaries } from "../../api/profile/profile-summary-service";
import {
  isGpcSignalActive,
  setStoredOptOut,
} from "../../policies/privacy-preference-service";

// Formats timestamps for settings rows and falls back cleanly when data is missing or invalid.
export function formatSettingsDateTime(value) {
  if (!value) return "Unknown";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";

  return date.toLocaleString();
}

// True when the visitor's browser/extension has set the Global Privacy Control
// signal (an opt-out of sale/sharing under CA/CO/CT law). Delegates to the
// shared privacy-preference-service, which owns detection, the persistent
// opt-out record, and the tracking gate.
export function detectGlobalPrivacyControl(
  nav = typeof navigator !== "undefined" ? navigator : null
) {
  return isGpcSignalActive(nav);
}

// Builds the short count label used in settings section headers.
export function getSettingsCountLabel(count, singularLabel, pluralLabel = `${singularLabel}s`) {
  const normalizedCount = Number.isFinite(count) ? count : 0;
  return `${normalizedCount} ${normalizedCount === 1 ? singularLabel : pluralLabel}`;
}

// Normalizes blocked-user rows returned from the backend into the shape used by the UI.
export function normalizeBlockedUsers(data) {
  if (!Array.isArray(data)) return [];

  return data
    .map((entry) => ({
      cognitoId: String(entry?.cognitoId || "").trim(),
      profileName: String(entry?.profileName || "").trim(),
      profilePicture: String(entry?.profilePicture || "").trim(),
      blockedAt: entry?.blockedAt || null,
    }))
    .filter((entry) => entry.cognitoId)
    .sort((a, b) => String(b.blockedAt || "").localeCompare(String(a.blockedAt || "")));
}

// Direct-chat chatPks have the deterministic shape `CHAT#PAIR#<sortedA>#<sortedB>`
// (see active.functions.js buildPairChatPk). When the Chat record is gone —
// for example after a block flow deletes it — getMyChat returns nothing, but
// the chatPk itself still encodes both members. Pulling the counterpart out
// of the chatPk lets the hidden-conversations list render the alias instead
// of the raw chatPk string.
export function extractCounterpartCognitoIdFromChatPk(chatPk, myUserId) {
  const normalizedChatPk = String(chatPk || "").trim();
  const normalizedMyUserId = String(myUserId || "").trim();
  if (!normalizedChatPk.startsWith("CHAT#PAIR#") || !normalizedMyUserId) {
    return null;
  }
  const parts = normalizedChatPk.split("#");
  if (parts.length < 4) return null;
  const memberIds = parts.slice(2).filter(Boolean);
  return memberIds.find((memberId) => memberId !== normalizedMyUserId) || null;
}

// Resolves hidden conversation titles into readable counterpart names for the current user.
export async function resolveConversationTitles(userId, chatIds) {
  const normalizedUserId = String(userId || "").trim();

  if (!normalizedUserId || !Array.isArray(chatIds) || !chatIds.length) {
    return new Map();
  }

  const chatRows = await Promise.all(
    chatIds.map(async (chatId) => {
      try {
        // Direct Chat.get is denied at the model level. The member-gated
        // getMyChat custom query verifies the caller is in chat_members
        // before returning the row.
        const [userChatResponse, chatResponse] = await Promise.all([
          client.models.UserChat.list({
            filter: {
              PK: { eq: `CONV#USER#${normalizedUserId}` },
              chat_id: { eq: chatId },
            },
            limit: 1,
          }),
          client.queries.getMyChat({ chatPk: chatId }),
        ]);

        const { data: userChatData, errors: userChatErrors } = userChatResponse ?? {};
        if (userChatErrors?.length) {
          throw new Error(userChatErrors.map((entry) => entry.message).join(", "));
        }

        if (chatResponse?.errors?.length) {
          throw new Error(chatResponse.errors.map((entry) => entry.message).join(", "));
        }

        const fallbackTitle = String(userChatData?.[0]?.name || "").trim() || chatId;
        const members = Array.isArray(chatResponse?.data?.chat_members)
          ? chatResponse.data.chat_members
          : [];
        const counterpartIdFromChat =
          members.find((memberId) => memberId !== normalizedUserId) || null;
        const counterpartIdFromPk = extractCounterpartCognitoIdFromChatPk(
          chatId,
          normalizedUserId
        );
        // getMyChat returns null when the underlying Chat row is gone (e.g.
        // block flow deletes it), so derive the counterpart from the chatPk
        // structure instead of leaving the alias unresolved.
        const counterpartId = counterpartIdFromChat || counterpartIdFromPk;

        return { chatId, fallbackTitle, counterpartId };
      } catch (error) {
        console.error("Failed to resolve conversation title:", { chatId, error });
        const counterpartId = extractCounterpartCognitoIdFromChatPk(
          chatId,
          normalizedUserId
        );
        return { chatId, fallbackTitle: chatId, counterpartId };
      }
    })
  );

  const counterpartIds = [
    ...new Set(
      chatRows
        .map((row) => String(row?.counterpartId || "").trim())
        .filter(Boolean)
    ),
  ];
  const profileSummaryByUserId = await getProfileSummaries(counterpartIds);

  return new Map(
    chatRows.map((row) => {
      const counterpartId = String(row?.counterpartId || "").trim();
      if (!counterpartId) return [row.chatId, row.fallbackTitle];

      const summary = profileSummaryByUserId.get(counterpartId);
      const profileName = String(
        summary?.profileName ||
          summary?.profile?.profileName ||
          summary?.profile?.name ||
          ""
      ).trim();

      return [row.chatId, profileName || row.fallbackTitle];
    })
  );
}

// Loads and manages the blocked-user and hidden-conversation state for the Settings screen.
export function useSettingsData() {
  const [myUserId, setMyUserId] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [hiddenConversations, setHiddenConversations] = useState([]);
  const [error, setError] = useState("");
  const [unblockTargetId, setUnblockTargetId] = useState("");
  const [restoreConversationTargetId, setRestoreConversationTargetId] = useState("");
  const [deleteAccountPending, setDeleteAccountPending] = useState(false);
  // Opt-out model: default to enabled so the toggle reads "on" until the
  // backend says otherwise (and for pre-feature accounts that have no
  // stored value).
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(true);
  const [notificationPrefSaving, setNotificationPrefSaving] = useState(false);
  // Opt-out model: sociopolitical answers are public unless the user turned
  // sharing off, so the toggle reads "on" until the backend says otherwise
  // (matches normalizeUserProfileDoc: absent reads as public).
  const [sociopoliticalPublic, setSociopoliticalPublic] = useState(true);
  const [sociopoliticalVisibilitySaving, setSociopoliticalVisibilitySaving] =
    useState(false);
  // Opt-in model for the sensitive identity attributes (orientation, religion,
  // ethnicity, politics): default private until the backend confirms sharing.
  const [sensitiveAttributesPublic, setSensitiveAttributesPublic] = useState(false);
  const [sensitiveVisibilitySaving, setSensitiveVisibilitySaving] = useState(false);
  // "Do not sell or share" opt-out (CPRA/GPC). Opt-out model: default false
  // (no opt-out on file) until the backend confirms one. An active GPC signal
  // supersedes the stored value — the UI shows the toggle locked on.
  const [privacyOptOut, setPrivacyOptOut] = useState(false);
  const [privacyOptOutSaving, setPrivacyOptOutSaving] = useState(false);

  const blockedCountLabel = useMemo(
    () => getSettingsCountLabel(blockedUsers.length, "user blocked", "users blocked"),
    [blockedUsers.length]
  );
  const hiddenConversationCountLabel = useMemo(
    () =>
      getSettingsCountLabel(
        hiddenConversations.length,
        "conversation hidden",
        "conversations hidden"
      ),
    [hiddenConversations.length]
  );

  useEffect(() => {
    let cancelled = false;

    async function loadUser() {
      try {
        const currentUser = await getCurrentUser();
        if (!cancelled) {
          setMyUserId(String(currentUser?.userId || "").trim());
        }
      } catch (loadUserError) {
        console.error("Failed to load current user for settings:", loadUserError);
        if (!cancelled) {
          setError(loadUserError?.message || "Failed to load current user.");
        }
      }
    }

    loadUser();

    return () => {
      cancelled = true;
    };
  }, []);

  const loadBlockedUsers = useCallback(async () => {
    const query = client?.queries?.listBlockedUsers;
    if (typeof query !== "function") {
      throw new Error("listBlockedUsers query is unavailable. Deploy backend schema updates.");
    }

    const response = await query({ limit: 200 });
    if (response?.errors?.length) {
      throw new Error(response.errors.map((entry) => entry.message).join(", "));
    }

    return normalizeBlockedUsers(response?.data);
  }, []);

  const loadProfilePreferences = useCallback(async (userId) => {
    const normalizedUserId = String(userId || "").trim();
    // Defaults mirror the backend's models: email enabled, sociopolitical
    // answers public (opt-out), sensitive attributes private (opt-in).
    const defaults = {
      emailNotificationsEnabled: true,
      sociopoliticalPublic: true,
      sensitiveAttributesPublic: false,
      privacyOptOut: false,
    };
    if (!normalizedUserId) return defaults;

    // A failed preference read must not blank the rest of the Settings screen.
    // Degrade to the defaults and let any real backend issue surface when the
    // user actually toggles.
    try {
      const query = client?.queries?.getUserProfile;
      if (typeof query !== "function") return defaults;

      const response = await query({ cognitoId: normalizedUserId });
      if (response?.errors?.length) {
        throw new Error(response.errors.map((entry) => entry.message).join(", "));
      }

      return {
        // Only an explicit `false` disables; missing/null reads as enabled.
        emailNotificationsEnabled: response?.data?.emailNotificationsEnabled !== false,
        // Opt-out model (matches backend normalization): only an explicit
        // `false` is private; missing/null reads as public.
        sociopoliticalPublic: response?.data?.sociopoliticalPublic !== false,
        // Sensitive attributes stay opt-in: only an explicit `true` is public.
        sensitiveAttributesPublic: response?.data?.sensitiveAttributesPublic === true,
        privacyOptOut: response?.data?.privacyOptOut === true,
      };
    } catch (preferenceError) {
      console.warn(
        "Failed to load profile preferences; defaulting (email on, answers public, sensitive private):",
        preferenceError
      );
      return defaults;
    }
  }, []);

  const loadVisibilityData = useCallback(async (userId) => {
    const normalizedUserId = String(userId || "").trim();

    if (!normalizedUserId) {
      return { hiddenConversations: [] };
    }

    const conversationEntries =
      await listHiddenConversationEntriesForUser(normalizedUserId);
    const chatIds = [
      ...new Set(conversationEntries.map((entry) => entry.chatId).filter(Boolean)),
    ];
    const titleByChatId = await resolveConversationTitles(normalizedUserId, chatIds);

    return {
      hiddenConversations: conversationEntries.map((entry) => ({
        ...entry,
        title: titleByChatId.get(entry.chatId) || entry.chatId,
      })),
    };
  }, []);

  const loadSettingsData = useCallback(
    async ({ silent = false } = {}) => {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError("");

      try {
        const [nextBlockedUsers, visibility, preferences] =
          await Promise.all([
            loadBlockedUsers(),
            loadVisibilityData(myUserId),
            loadProfilePreferences(myUserId),
          ]);

        setBlockedUsers(nextBlockedUsers);
        setHiddenConversations(visibility.hiddenConversations);
        setEmailNotificationsEnabled(preferences.emailNotificationsEnabled);
        setSociopoliticalPublic(preferences.sociopoliticalPublic);
        setSensitiveAttributesPublic(preferences.sensitiveAttributesPublic);
        setPrivacyOptOut(preferences.privacyOptOut);
      } catch (loadError) {
        console.error("Failed to load settings data:", loadError);
        setError(loadError?.message || "Failed to load settings data.");
        setBlockedUsers([]);
        setHiddenConversations([]);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [loadBlockedUsers, loadVisibilityData, loadProfilePreferences, myUserId]
  );

  useEffect(() => {
    if (!myUserId) return;
    loadSettingsData();
  }, [myUserId, loadSettingsData]);

  const handleUnblock = useCallback(
    async (cognitoId) => {
      const userId = String(cognitoId || "").trim();
      if (!userId || unblockTargetId) return;

      setUnblockTargetId(userId);
      setError("");

      try {
        const mutation = client?.mutations?.unblockUser;
        if (typeof mutation !== "function") {
          throw new Error("unblockUser mutation is unavailable. Deploy backend schema updates.");
        }

        const response = await mutation({ blockedUserId: userId });
        if (response?.errors?.length) {
          throw new Error(response.errors.map((entry) => entry.message).join(", "));
        }

        setBlockedUsers((previousBlockedUsers) =>
          previousBlockedUsers.filter((entry) => entry.cognitoId !== userId)
        );
      } catch (unblockError) {
        console.error("Failed to unblock user:", unblockError);
        setError(unblockError?.message || "Failed to unblock user.");
      } finally {
        setUnblockTargetId("");
      }
    },
    [unblockTargetId]
  );

  const handleUnhideConversation = useCallback(
    async (chatId) => {
      const normalizedChatId = String(chatId || "").trim();
      if (!myUserId || !normalizedChatId || restoreConversationTargetId) return;

      setRestoreConversationTargetId(normalizedChatId);
      setError("");

      try {
        await unhideConversationForUser({
          userId: myUserId,
          chatPk: normalizedChatId,
        });
        setHiddenConversations((previousConversations) =>
          previousConversations.filter((entry) => entry.chatId !== normalizedChatId)
        );
        publishCommunicationEvent({
          type: "chat_visibility_changed",
          chatPk: normalizedChatId,
          status: "visible",
          activityAt: new Date().toISOString(),
        });
      } catch (restoreError) {
        console.error("Failed to unhide conversation:", restoreError);
        setError(restoreError?.message || "Failed to unhide conversation.");
      } finally {
        setRestoreConversationTargetId("");
      }
    },
    [myUserId, restoreConversationTargetId]
  );

  const handleToggleEmailNotifications = useCallback(async () => {
    if (!myUserId || notificationPrefSaving) return;

    const previousValue = emailNotificationsEnabled;
    const nextValue = !previousValue;

    // Optimistic flip so the switch feels instant; roll back on failure.
    setEmailNotificationsEnabled(nextValue);
    setNotificationPrefSaving(true);
    setError("");

    try {
      const mutation = client?.mutations?.setEmailNotificationPreference;
      if (typeof mutation !== "function") {
        throw new Error(
          "setEmailNotificationPreference mutation is unavailable. Deploy backend schema updates."
        );
      }

      const response = await mutation({
        cognitoId: myUserId,
        enabled: nextValue,
      });
      if (response?.errors?.length) {
        throw new Error(response.errors.map((entry) => entry.message).join(", "));
      }

      const confirmed = response?.data?.emailNotificationsEnabled;
      if (typeof confirmed === "boolean") {
        setEmailNotificationsEnabled(confirmed);
      }
    } catch (toggleError) {
      console.error("Failed to update email notification preference:", toggleError);
      setError(
        toggleError?.message || "Failed to update email notification preference."
      );
      setEmailNotificationsEnabled(previousValue);
    } finally {
      setNotificationPrefSaving(false);
    }
  }, [emailNotificationsEnabled, myUserId, notificationPrefSaving]);

  const handleToggleSociopoliticalVisibility = useCallback(async () => {
    if (!myUserId || sociopoliticalVisibilitySaving) return;

    const previousValue = sociopoliticalPublic;
    const nextValue = !previousValue;

    // Optimistic flip so the switch feels instant; roll back on failure.
    setSociopoliticalPublic(nextValue);
    setSociopoliticalVisibilitySaving(true);
    setError("");

    try {
      const mutation = client?.mutations?.setSociopoliticalVisibility;
      if (typeof mutation !== "function") {
        throw new Error(
          "setSociopoliticalVisibility mutation is unavailable. Deploy backend schema updates."
        );
      }

      const response = await mutation({
        cognitoId: myUserId,
        isPublic: nextValue,
      });
      if (response?.errors?.length) {
        throw new Error(response.errors.map((entry) => entry.message).join(", "));
      }

      const confirmed = response?.data?.sociopoliticalPublic;
      if (typeof confirmed === "boolean") {
        setSociopoliticalPublic(confirmed);
      }
    } catch (toggleError) {
      console.error("Failed to update sociopolitical visibility:", toggleError);
      setError(
        toggleError?.message || "Failed to update sociopolitical visibility."
      );
      setSociopoliticalPublic(previousValue);
    } finally {
      setSociopoliticalVisibilitySaving(false);
    }
  }, [sociopoliticalPublic, myUserId, sociopoliticalVisibilitySaving]);

  const handleToggleSensitiveAttributesVisibility = useCallback(async () => {
    if (!myUserId || sensitiveVisibilitySaving) return;

    const previousValue = sensitiveAttributesPublic;
    const nextValue = !previousValue;

    // Optimistic flip so the switch feels instant; roll back on failure.
    setSensitiveAttributesPublic(nextValue);
    setSensitiveVisibilitySaving(true);
    setError("");

    try {
      const mutation = client?.mutations?.setSensitiveAttributesVisibility;
      if (typeof mutation !== "function") {
        throw new Error(
          "setSensitiveAttributesVisibility mutation is unavailable. Deploy backend schema updates."
        );
      }

      const response = await mutation({
        cognitoId: myUserId,
        isPublic: nextValue,
      });
      if (response?.errors?.length) {
        throw new Error(response.errors.map((entry) => entry.message).join(", "));
      }

      const confirmed = response?.data?.sensitiveAttributesPublic;
      if (typeof confirmed === "boolean") {
        setSensitiveAttributesPublic(confirmed);
      }
    } catch (toggleError) {
      console.error("Failed to update sensitive attribute visibility:", toggleError);
      setError(
        toggleError?.message || "Failed to update sensitive attribute visibility."
      );
      setSensitiveAttributesPublic(previousValue);
    } finally {
      setSensitiveVisibilitySaving(false);
    }
  }, [sensitiveAttributesPublic, myUserId, sensitiveVisibilitySaving]);

  const handleTogglePrivacyOptOut = useCallback(async () => {
    if (!myUserId || privacyOptOutSaving) return;

    const previousValue = privacyOptOut;
    const nextValue = !previousValue;

    // Optimistic flip so the switch feels instant; roll back on failure.
    setPrivacyOptOut(nextValue);
    setPrivacyOptOutSaving(true);
    setError("");

    try {
      const mutation = client?.mutations?.setPrivacyOptOut;
      if (typeof mutation !== "function") {
        throw new Error(
          "setPrivacyOptOut mutation is unavailable. Deploy backend schema updates."
        );
      }

      const response = await mutation({
        cognitoId: myUserId,
        optOut: nextValue,
        source: "manual",
      });
      if (response?.errors?.length) {
        throw new Error(response.errors.map((entry) => entry.message).join(", "));
      }

      const confirmed = response?.data?.privacyOptOut;
      if (typeof confirmed === "boolean") {
        setPrivacyOptOut(confirmed);
      }
      // Keep the anonymous/local record in step with the account-level one so
      // signed-out browsing on this device honors the same election.
      setStoredOptOut(typeof confirmed === "boolean" ? confirmed : nextValue);
    } catch (toggleError) {
      console.error("Failed to update privacy opt-out:", toggleError);
      setError(toggleError?.message || "Failed to update privacy opt-out.");
      setPrivacyOptOut(previousValue);
    } finally {
      setPrivacyOptOutSaving(false);
    }
  }, [privacyOptOut, myUserId, privacyOptOutSaving]);

  const handleDeleteAccount = useCallback(async () => {
    if (!myUserId || deleteAccountPending) return false;

    setDeleteAccountPending(true);
    setError("");

    const beforeUnloadHandler = (event) => {
      event.preventDefault();
      event.returnValue =
        "Account deletion is being queued. Please wait until the request is accepted.";
      return event.returnValue;
    };
    if (typeof window !== "undefined") {
      window.addEventListener("beforeunload", beforeUnloadHandler);
    }

    try {
      const mutation = client?.mutations?.deleteMyAccount;
      if (typeof mutation !== "function") {
        throw new Error("deleteMyAccount mutation is unavailable. Deploy backend schema updates.");
      }

      const response = await mutation({});
      if (response?.errors?.length) {
        throw new Error(response.errors.map((entry) => entry.message).join(", "));
      }

      try {
        window?.localStorage?.clear?.();
        window?.sessionStorage?.clear?.();
      } catch {}

      if (typeof window !== "undefined") {
        window.removeEventListener("beforeunload", beforeUnloadHandler);
      }

      if (typeof window?.location?.assign === "function") {
        window.location.assign("/");
      }

      return true;
    } catch (deleteError) {
      console.error("Failed to delete account:", deleteError);
      setError(deleteError?.message || "Failed to delete account.");
      if (typeof window !== "undefined") {
        window.removeEventListener("beforeunload", beforeUnloadHandler);
      }
      setDeleteAccountPending(false);
      return false;
    }
  }, [deleteAccountPending, myUserId]);

  return {
    myUserId,
    loading,
    refreshing,
    blockedUsers,
    hiddenConversations,
    error,
    unblockTargetId,
    restoreConversationTargetId,
    deleteAccountPending,
    emailNotificationsEnabled,
    notificationPrefSaving,
    sociopoliticalPublic,
    sociopoliticalVisibilitySaving,
    sensitiveAttributesPublic,
    sensitiveVisibilitySaving,
    privacyOptOut,
    privacyOptOutSaving,
    blockedCountLabel,
    hiddenConversationCountLabel,
    disableRefresh:
      loading ||
      refreshing ||
      Boolean(unblockTargetId) ||
      Boolean(restoreConversationTargetId) ||
      deleteAccountPending,
    loadSettingsData,
    handleUnblock,
    handleUnhideConversation,
    handleToggleEmailNotifications,
    handleToggleSociopoliticalVisibility,
    handleToggleSensitiveAttributesVisibility,
    handleTogglePrivacyOptOut,
    handleDeleteAccount,
  };
}
